import { useState } from "react";
import { InvoicePayment } from "../../../bitmaker/schemas/Billing/Payment";
import { Invoice } from "../../../bitmaker/schemas/Billing/Invoice";
import {
  Button,
  Popconfirm,
  Table,
  Space,
  TableColumnsType,
  Form,
  Modal,
  Select,
  DatePicker,
  InputNumber,
  Input,
  message as antdMessage,
} from "antd";
import { LoadingOutlined, EditOutlined } from "@ant-design/icons";
import { Plus, Check } from "lucide-react";
import dayjs from "dayjs";
import { formatCurrency } from "../../../utils";
import { MyFormItem } from "../MyFormItem";
import { usePaymentMethods } from "../../../hooks/usePaymentMethods";
import { useInvoicePayments } from "../../../hooks/useInvoicePayments";
import { useApiError } from "../../../hooks/useApiError";
import {
  BitmakerAPIClient,
  HTTPMethod,
  methodMap,
} from "../../../services/BitmakerApp";

interface InvoicePaymentsProps {
  invoice: Invoice;
  onInvoiceUpdate: () => void;
}

export default function InvoicePayments({
  invoice,
  onInvoiceUpdate,
}: InvoicePaymentsProps) {
  const {
    invoicePayments,
    loadingInvoicePayments,
    fetchInvoicePaymentsError,
    refetchInvoicePayments,
  } = useInvoicePayments(invoice.id);

  const currencyPrefix = invoice.currency === "PEN" ? "S/" : "$";

  const { paymentMethods, loadingPaymentMethods, fetchPaymentMethodsError } =
    usePaymentMethods();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form] = Form.useForm();

  const [currentPayment, setCurrentPayment] = useState<InvoicePayment | null>(
    null,
  );

  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { handleAPIError } = useApiError();

  const amountDue = invoice.totalAmount - invoice.amountPaid;

  const handleAddPayment = () => {
    setCurrentPayment(null);
    setSubmitError(null);

    form.resetFields();

    form.setFieldsValue({
      paymentAmount: amountDue > 0 ? amountDue.toFixed(2) : 0,
      paymentDate: dayjs(),
      paymentMethod: undefined,
      paymentNotes: undefined,
    });

    setIsModalOpen(true);
  };

  const handleEditPayment = (payment: InvoicePayment) => {
    setCurrentPayment(payment);

    form.setFieldsValue({
      paymentAmount: payment.amount,
      paymentDate: dayjs(payment.paymentDate),
      paymentMethod: payment.paymentMethodId,
      paymentNotes: payment.paymentNotes,
    });

    setIsModalOpen(true);
    setSubmitError(null);
  };

  const handleCancelModal = () => {
    setSubmitError(null);
    setIsModalOpen(false);
    setCurrentPayment(null);
  };

  const handleSavePayment = (values: any) => {
    setSubmitError(null);
    setIsSubmitting(true);

    const payload = {
      amount: values.paymentAmount,
      paymentDate: values.paymentDate.format("YYYY-MM-DD"),
      paymentMethodId: values.paymentMethod,
      paymentNotes: values.paymentNotes || null,
    };

    const isEditing = currentPayment !== null;

    const endpoint = isEditing
      ? { method: "put", url: `/billing/payments/${currentPayment.id}` }
      : { method: "post", url: `/billing/invoices/${invoice.id}/payments` };
    const method = endpoint.method as HTTPMethod;

    methodMap[method](endpoint.url, payload, (response) => {
      const { isError, message: apiMessage } = handleAPIError(response);

      if (isError) {
        console.error(
          `Error ${isEditing ? "updating" : "creating"} payment:`,
          apiMessage,
        );
        setSubmitError(
          `Error ${
            isEditing ? "updating" : "creating"
          } payment. Please try again.`,
        );
        setIsSubmitting(false);
        return;
      }

      antdMessage.success(
        `Payment ${isEditing ? "updated" : "created"} successfully!`,
      );

      setIsModalOpen(false);
      setCurrentPayment(null);
      refetchInvoicePayments();
      setIsSubmitting(false);
      onInvoiceUpdate();
    });
  };

  const handleDeletePayment = (paymentId: number) => {
    BitmakerAPIClient.delete(
      `/billing/payments/${paymentId}`,
      {},
      (response) => {
        const { isError, message: apiMessage } = handleAPIError(response);

        if (isError) {
          console.error("Error deleting payment:", apiMessage);
          antdMessage.error("Failed to delete payment. Please try again.");
          return;
        }

        antdMessage.success("Payment deleted successfully.");
        refetchInvoicePayments();
        onInvoiceUpdate();
      },
    );
  };

  const paymentsTableColumns: TableColumnsType<InvoicePayment> = [
    {
      title: "Date",
      dataIndex: "paymentDate",
      key: "paymentDate",
      render: (date: string) => dayjs(date).format("MMM DD, YYYY"),
    },
    {
      title: "Payment Method",
      key: "paymentMethodName",
      render: (_, record: any) => record.paymentMethod?.name || "N/A",
    },
    {
      title: "Internal Notes",
      dataIndex: "paymentNotes",
      key: "paymentNotes",
    },
    {
      title: "Line Total",
      key: "amount",
      align: "right",
      render: (_, record: InvoicePayment) => (
        <div>
          {formatCurrency(record.amount, invoice.currency, invoice.language)}
        </div>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      render: (_, record: InvoicePayment) => (
        <Space size="middle">
          <button
            onClick={() => handleEditPayment(record)}
            className="btn btn-small bg-indigo-600 text-white hover:bg-indigo-700"
          >
            <EditOutlined size={14} />
            <span>Edit</span>
          </button>
          <Popconfirm
            title="Are you sure to delete this payment?"
            onConfirm={() => handleDeletePayment(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" danger>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const PaymentModal = () => {
    return (
      <Modal
        title={currentPayment ? "Edit Payment" : "Add a Payment"}
        open={isModalOpen}
        onCancel={handleCancelModal}
        afterClose={() => {
          form.resetFields();
        }}
        footer={
          <div className="flex justify-end gap-2 mt-6">
            {/* CANCEL BUTTON */}
            <button onClick={handleCancelModal} className="btn btn-gray">
              Cancel
            </button>

            {/* SAVE BUTTON */}
            <button
              form="addPaymentForm"
              type="submit"
              className="btn btn-green"
              disabled={isSubmitting}
            >
              {isSubmitting ? <LoadingOutlined /> : <Check size={14} />}
              <span>
                {isSubmitting
                  ? "Saving..."
                  : currentPayment
                  ? "Update"
                  : "Save"}
              </span>
            </button>
          </div>
        }
        className="custom-modal"
      >
        {/* Amount Due */}
        <div className="mb-4">
          Amount Due:{" "}
          <span className="font-semibold">
            {formatCurrency(amountDue, invoice.currency, invoice.language)}
          </span>
        </div>

        {submitError && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg text-sm">
            {submitError}
          </div>
        )}

        <Form
          id="addPaymentForm"
          form={form}
          layout="vertical"
          onFinish={handleSavePayment}
          className="mt-4"
        >
          <div className="flex flex-col space-y-6">
            {/* Payment Amount */}
            <MyFormItem
              label="Payment Amount"
              name="paymentAmount"
              rules={[
                { required: true, message: "Please enter the payment amount." },
              ]}
            >
              <InputNumber
                min={0}
                max={amountDue > 0 ? amountDue : invoice.totalAmount}
                step={0.01}
                precision={2}
                style={{ width: "100%" }}
                formatter={(value) =>
                  value !== undefined ? `${currencyPrefix}${value}` : ""
                }
                parser={(displayValue) => {
                  const cleanedValue = displayValue?.replace(
                    currencyPrefix,
                    "",
                  );
                  if (!cleanedValue) return undefined as any;
                  return parseFloat(cleanedValue);
                }}
              />
            </MyFormItem>

            {/* Payment Date */}
            <MyFormItem
              label="Payment Date"
              name="paymentDate"
              rules={[{ required: true, message: "Please select a date" }]}
            >
              <DatePicker style={{ width: "100%" }} />
            </MyFormItem>

            {/* Method */}
            {fetchPaymentMethodsError && (
              <p className="text-sm text-red-500 mb-1">
                {fetchPaymentMethodsError}
              </p>
            )}
            <MyFormItem
              label="Payment Method"
              name="paymentMethod"
              rules={[
                { required: true, message: "Please select a payment method" },
              ]}
            >
              <Select
                placeholder="Select method"
                loading={loadingPaymentMethods}
                disabled={loadingPaymentMethods || paymentMethods.length === 0}
              >
                {paymentMethods.map((method) => (
                  <Select.Option key={method.id} value={method.id}>
                    {method.name}
                  </Select.Option>
                ))}
              </Select>
            </MyFormItem>

            {/* Payment Notes (Opcional) */}
            <MyFormItem label="Payment notes (Optional)" name="paymentNotes">
              <Input.TextArea
                rows={2}
                placeholder="e.g. bank transfer number"
              />
            </MyFormItem>
          </div>
        </Form>
      </Modal>
    );
  };

  return (
    <div className="mt-6 mb-12">
      {/* Payments Header */}
      <div className="flex justify-between items-center mb-6">
        {/* Title */}
        <h3 className="text-lg font-semibold text-gray-800">
          All Payments For Invoice # {invoice.invoiceNumber}
        </h3>
        {/* Add Payment Button */}
        <div className="flex justify-start">
          <button
            onClick={handleAddPayment}
            className="btn btn-green-2"
            disabled={loadingInvoicePayments}
          >
            <Plus size={14} />
            <span>Add Payment</span>
          </button>
        </div>
      </div>

      {fetchInvoicePaymentsError && (
        <div className="mt-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg text-sm">
          {fetchInvoicePaymentsError}
        </div>
      )}

      {/* Invoice Payments Table */}
      <Table
        columns={paymentsTableColumns}
        dataSource={invoicePayments}
        rowKey="id"
        loading={loadingInvoicePayments}
        pagination={false}
        size="middle"
      />

      {/* Add/Edit Invoice Payment Modal */}
      <PaymentModal />
    </div>
  );
}
